/* eslint-disable @atlaskit/platform/use-recommended-utils */
import FeatureGates from '@atlaskit/feature-gate-js-client';

export enum ConvoAIWebSocketServerMessageType {
	TranscriptionResponseDelta = 'transcription.response.delta',
	TranscriptionItemCreated = 'transcription.item.created',
}

export enum ConvoAIWebSocketClientMessageType {
	AudioBufferAppend = 'audio.buffer.append',
	AudioBufferCommit = 'audio.buffer.commit',
	AudioSessionUpdate = 'audio.session.update',
}

export type ConvoAIWebSocketServerMessage =
	| {
			content_index: number;
			delta: string;
			type: ConvoAIWebSocketServerMessageType.TranscriptionResponseDelta;
	  }
	| {
			type: ConvoAIWebSocketServerMessageType.TranscriptionItemCreated;
	  };

export class ConvoAIWebSocketAPI {
	private socket: WebSocket;

	constructor(
		cloudID: string,
		product: string,
		experience: string,
		private readonly onMessage: (event: ConvoAIWebSocketServerMessage) => void,
	) {
		this.socket = new WebSocket(
			`/wss/${cloudID}/ai-feature/audio/realtime-transcriptions?product=${product}&experience=${experience}`,
		);
		this.socket.addEventListener('message', this.onServerMessage);
	}

	updateTranscriptionConfig(
		instructions: string = '',
		noise_detection: string = 'near_field',
		vad_type: string = '	',
		eagerness: string = 'medium',
		model: string = 'gpt-4o-transcribe',
		include: string[] = ['item.input_audio_transcription.logprobs'],
	): void {
		this.socket.send(
			JSON.stringify({
				type: 'audio.session.update',
				model: FeatureGates.getExperimentValue('ai-stt-prompt', 'model', model),
				include: FeatureGates.getExperimentValue('ai-stt-prompt', 'include', include),
				vad_type: FeatureGates.getExperimentValue('ai-stt-prompt', 'vad_type', vad_type),
				eagerness: FeatureGates.getExperimentValue('ai-stt-prompt', 'eagerness', eagerness),
				instructions: FeatureGates.getExperimentValue(
					'ai-stt-prompt',
					'instructions',
					instructions,
				),
				noise_detection: FeatureGates.getExperimentValue(
					'ai-stt-prompt',
					'noise_detection',
					noise_detection,
				),
			}),
		);
	}

	sendAudio(audio: string): void {
		this.socket.send(
			JSON.stringify({ audio, type: ConvoAIWebSocketClientMessageType.AudioBufferAppend }),
		);
	}

	commitAudio(): void {
		this.socket.send(JSON.stringify({ type: ConvoAIWebSocketClientMessageType.AudioBufferCommit }));
	}

	connect(): Promise<ConvoAIWebSocketAPI> {
		return new Promise((resolve, reject) => {
			this.socket.onopen = () => resolve(this);
			this.socket.onerror = (event) => reject(event);
		});
	}

	onServerMessage = (event: MessageEvent): void => {
		const data = JSON.parse(event.data) as ConvoAIWebSocketServerMessage;
		this.onMessage(data);
	};

	disconnect(): void {
		this.socket?.close();
	}

	static create(
		cloudID: string,
		product: string,
		experience: string,
		onMessage: (event: ConvoAIWebSocketServerMessage) => void,
	): Promise<ConvoAIWebSocketAPI> {
		const api = new ConvoAIWebSocketAPI(cloudID, product, experience, onMessage);
		return api.connect();
	}
}
